export const categories = [
    { id: 'all', name: '전체' },
    { id: 'face', name: '얼굴 분석' },
    { id: 'personality', name: '성향 분석' },
    { id: 'style', name: '스타일/추구미' },
];

export const insights = [
    {
        id: 'why-first-impression-matters',
        title: '첫인상을 결정짓는 0.1초의 구조',
        summary: '우리의 뇌는 0.1초 만에 상대를 판단합니다. 그 짧은 순간, 얼굴의 어떤 요소가 신뢰감과 호감을 결정할까요?',
        category: 'face',
        date: '2025-01-20',
        thumbnail: '/assets/insights/thumb_first_impression.jpg',
        content: `
# 첫인상을 결정짓는 0.1초의 구조

우리는 누군가를 처음 만났을 때, 아주 짧은 순간에 그 사람에 대한 많은 것을 판단합니다. 심리학 연구에 따르면, **첫인상이 형성되는 데 걸리는 시간은 단 0.1초**라고 합니다.

## 뇌가 얼굴을 인식하는 방식

우리의 뇌, 특히 편도체(Amygdala)는 생존 본능과 연결되어 있어 낯선 대상을 보자마자 '안전한가?' 혹은 '위협적인가?'를 본능적으로 판단합니다. 이때 가장 중요한 역할을 하는 것이 바로 **얼굴의 비율과 표정**입니다.

### 신뢰감을 주는 얼굴의 특징

1. **대칭성**: 좌우 대칭이 잘 맞는 얼굴은 유전적 건강함을 암시하며, 이는 무의식적인 호감으로 이어집니다.
2. **눈의 편안함**: 눈꼬리가 지나치게 올라가거나 내려가지 않은, 편안한 눈매는 경계심을 낮춥니다.
3. **미소**: 입꼬리가 살짝 올라간 표정은 '접근 가능성(Approachability)' 신호를 보냅니다.

## TETO와 EGEN으로 보는 첫인상

StartUs의 분석 모델인 **TETO(Testosterone)**와 **EGEN(Estrogen)** 비율은 첫인상의 '분위기'를 결정하는 핵심 지표입니다.

- **TETO가 높은 얼굴**: 강인하고 주도적인 인상을 줍니다. 리더십이 있어 보이지만, 자칫 차가워 보일 수 있습니다.
- **EGEN이 높은 얼굴**: 부드럽고 친화적인 인상을 줍니다. 다가가기 쉬워 보이지만, 카리스마가 부족해 보일 수 있습니다.

중요한 것은 **나의 타고난 비율을 이해하고, 상황에 맞게 연출하는 것**입니다.

## 결론

첫인상은 바꿀 수 없는 운명이 아닙니다. 나의 얼굴이 가진 특징을 정확히 분석하고, 표정과 스타일링을 통해 원하는 이미지를 만들어가는 과정. 그것이 바로 퍼스널 브랜딩의 시작입니다.
        `,
    },
    {
        id: 'seb-personality-structure',
        title: 'MBTI보다 구조적인 성향 분석이 필요한 이유',
        summary: 'MBTI는 유용하지만, 사람의 복잡한 내면을 모두 설명하기엔 부족합니다. SEB 모델로 더 깊이 있는 나를 발견하세요.',
        category: 'personality',
        date: '2025-01-18',
        thumbnail: '/assets/insights/thumb_seb.jpg',
        content: `
# MBTI보다 구조적인 성향 분석이 필요한 이유

MBTI는 대중적으로 가장 널리 알려진 성격 유형 검사입니다. 16가지 유형으로 사람을 분류하여 서로를 이해하는 데 큰 도움을 주었죠. 하지만, "나는 I인데 가끔 E 같아"라고 느껴본 적 없으신가요?

## 유형론(Typology)의 한계

MBTI는 이분법적인 **유형론**에 기반합니다. 외향(E) 아니면 내향(I)으로 딱 잘라 구분하죠. 하지만 실제 인간의 성격은 스펙트럼에 가깝습니다. 51%의 외향과 99%의 외향은 전혀 다른 모습을 보이지만, MBTI에서는 같은 'E'로 분류됩니다.

## 구조적 분석: SEB 모델

StartUs는 이러한 한계를 보완하기 위해 **SEB (Stability, Empathy, Belief)** 모델을 제안합니다. 이는 성격을 '유형'이 아닌 '구조'로 바라보는 관점입니다.

### 1. Stability (안정성)
외부 자극에 대해 얼마나 감정적으로 동요하는지를 나타냅니다.
- 높음: 차분하고 일관성 있음
- 낮음: 예민하고 반응성이 높음 (예술적 감수성과 연결)

### 2. Empathy (공감도)
타인의 감정에 얼마나 깊이 이입하는지, 혹은 논리적 해결을 우선하는지를 봅니다.
- 높음: 관계 지향적, 따뜻함
- 낮음: 과업 지향적, 냉철함

### 3. Belief (신념/주관)
자신의 가치관을 얼마나 강하게 밀고 나가는지를 의미합니다.
- 높음: 주관이 뚜렷하고 리더십 있음
- 낮음: 유연하고 수용적임

## 나를 입체적으로 이해하기

사람은 상황에 따라 다른 가면(Persona)을 씁니다. SEB 모델은 내가 편안할 때의 본질적인 성향과, 사회생활을 하며 만들어진 성향을 구분하여 분석합니다.

단순히 "너는 ENFP야"라고 규정하는 것을 넘어, **"너는 공감도가 높지만, 결정적인 순간에는 논리적 신념이 앞서는 사람이야"**라고 설명할 수 있을 때, 비로소 진정한 자기 이해가 시작됩니다.
        `,
    },
    {
        id: 'chugumi-is-result',
        title: '추구미는 선택이 아니라 누적된 결과다',
        summary: '우리가 무언가를 "추구"할 때, 그것은 단순한 취향이 아닙니다. 살아온 환경과 결핍, 그리고 욕망이 빚어낸 결과물입니다.',
        category: 'style',
        date: '2025-01-15',
        thumbnail: '/assets/insights/thumb_chugumi.jpg',
        content: `
# 추구미는 선택이 아니라 누적된 결과다

요즘 SNS에서 유행하는 단어, **'추구미'**. 내가 추구하는 이미지나 분위기를 뜻하는 신조어입니다. "내 추구미는 힙한 스트릿 무드야", "나는 청순한 첫사랑 재질이 추구미야"라고 말하곤 하죠.

## 취향은 우연이 아니다

프랑스의 사회학자 피에르 부르디외는 그의 저서 <구별짓기>에서 **"취향은 계급이다"**라고 말했습니다. 우리가 아름답다고 느끼는 것, 갖고 싶다고 느끼는 것은 순수한 개인의 선호가 아니라, 내가 속한 사회적 환경과 자본(문화 자본)의 영향을 받는다는 것이죠.

우리의 '추구미'도 마찬가지입니다.

### 결핍과 동경

우리는 종종 **나에게 없는 것을 동경**합니다.
- 너무나 모범생으로 살아온 사람은 자유분방한 '퇴폐미'를 추구하기도 하고,
- 불안정한 삶을 살아온 사람은 단정하고 깔끔한 '올드머니 룩'을 추구하기도 합니다.

즉, 추구미는 **나의 결핍을 채우려는 무의식적인 시도**일 수 있습니다.

## 스타일 분석이 알려주는 것

StartUs의 스타일 분석은 단순히 "당신에게 어울리는 옷은 이것입니다"라고 추천하는 데 그치지 않습니다. 당신이 **왜 그런 스타일을 선호하는지**, 그리고 **그 스타일이 타인에게 어떤 신호를 보내는지**를 분석합니다.

- **직선적인 스타일**을 선호한다면: 명확함, 효율성, 도시적인 세련됨을 중요시하는 성향
- **곡선적인 스타일**을 선호한다면: 유연함, 편안함, 자연스러움을 중요시하는 성향

## 진정한 '나다움'을 찾아서

유행하는 추구미를 쫓는 것은 쉽습니다. 하지만 그것이 정말 나에게 어울리는지, 나의 내면과 일치하는지는 다른 문제입니다.

진정한 스타일은 **나의 타고난 외형(하드웨어)과 내면의 성향(소프트웨어)이 조화를 이룰 때** 완성됩니다. 남들이 좋다는 추구미가 아니라, 나의 삶이 묻어나는 나만의 분위기를 찾아보세요.
        `,
    },
];

export const getInsightById = (id) => insights.find((insight) => insight.id === id);

export const getInsightsByCategory = (category) => {
    if (category === 'all') return insights;
    return insights.filter((insight) => insight.category === category);
};
